import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import {
	MaybeSuspenseBoundary,
	type MessageProps,
} from '@post-office/placement-common/utils/message-renderer';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { ConfluenceHomeBannerPlacementSkeleton } from './skeleton';

import { MessageRenderer, type renderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { type ConfluenceHomeBannerInitialData } from '../api/types';

import { type ConfluenceHomeBannerPlacement } from '../contracts/Placement';

import { type ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

export type Message = MessageProps<typeof renderer>;

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

/**
 * Confluence Home Banner does not have IntlProvider, all it's translations are managed by Confluence
 *
 * @see confluence/next/packages/i18n/src/messages.ts
 */
const ConfluenceHomeBanner: (
	props: ErrorBoundariedProps<ConfluenceHomeBannerPlacement>,
) => ReactNode = createPlacementComponent<ConfluenceHomeBannerPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData }) => {
		const { data, isLoading } = useInitialData<ConfluenceHomeBannerInitialData>(initialData);
		// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-unsafe-assignment
		const [{ message, status }, setMessageState] = useState<{
			// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
			message: Message | undefined;
			status: 'loading' | 'done';
		}>(() => {
			if (preloadedInitialData) {
				return preloadedInitialData?.messages?.length > 0
					? {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							message: preloadedInitialData?.messages[0] as Message,
							status: 'done',
						}
					: { message: undefined, status: 'done' };
			}

			return { message: undefined, status: 'loading' };
		});

		const handleOnDismissed = useCallback(() => {
			setMessageState({ message: undefined, status: 'done' });
		}, []);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const newMessage = ((data?.messages || []) as Message[]).filter(
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
					(dataMessage) => !excludedMessages.has(dataMessage.messageTemplateId),
				)[0];

				setMessageState({
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					message: newMessage,
					status: 'done',
				});
			}
		}, [isLoading, isDone, data]);

		if (status === 'loading') {
			return <ConfluenceHomeBannerPlacementSkeleton />;
		}

		return (
			<>
				{message && (
					// Adding @ts-ingore to suppress the error in confluence typecheck and below error is not thrown in post-office typecheck
					// This error exists because confluence is using react 16 and post-office is using react 18
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore - 'ReactNode | ReactElement<any, any>' is not a valid JSX element.
					<MessageRenderer
						{...message}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
						id={message.messageInstanceId}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
						messageInstanceId={message.messageInstanceId}
						onDismissed={handleOnDismissed}
						SuspenseBoundary={MaybeSuspenseBoundary}
					/>
				)}
			</>
		);
	}),
	{
		choreographer: {
			// Disabling this placement from being choreographed as it is not a pop-up
			isDefaultEnabled: false,
		},
	},
);

export default ConfluenceHomeBanner;
